import { Link } from "react-router-dom";
import { ChefHat, Utensils, UserRound, PartyPopper, MessageCircle, CheckCircle2, ArrowUpRight, XCircle, Shield, Clock, Award, Sparkles, Smile } from "lucide-react";
import { waLink, WHATSAPP_LINK, BRAND } from "../brand";
import { Seo } from "../components/Seo";

const SERVICES = [
  {
    icon: Utensils,
    title: "Kitchen Prep",
    desc: "Chopping, cutting, and complete pre-preparation of ingredients — so your kitchen is ready the moment you decide to cook.",
    bullets: ["Vegetables washed & cut to your size", "Dals soaked, dough kneaded", "Marinades & pastes prepared"],
  },
  {
    icon: ChefHat,
    title: "Cooking",
    desc: "North Indian, multi-cuisine and more. A trained cook prepares a full meal at your stove, using your ingredients.",
    bullets: ["North Indian & regional cuisines", "Diet-friendly on request", "Your ingredients, your taste"],
  },
  {
    icon: UserRound,
    title: "On-Demand Cook",
    desc: "A professional cook at your convenience — book for an hour, an evening, or a whole afternoon.",
    bullets: ["Same-day slots", "Verified & background-checked", "Available in 30 minutes*"],
    accent: "dark",
  },
  {
    icon: PartyPopper,
    title: "Family Gathering & Party",
    desc: "Two trained cooks handle prep and cooking end-to-end for up to 20 guests. Minimum 3-hour booking.",
    bullets: ["Up to 20 people", "2 trained cooks", "Minimum 3 hours booking"],
  },
];

const WHY_US = [
  { icon: Shield, t: "Verified & Background Checked Staff" },
  { icon: Award, t: "Trained Kitchen Professionals" },
  { icon: Sparkles, t: "Hygienic Kitchen Practices" },
  { icon: CheckCircle2, t: "Covered & Insured" },
  { icon: Clock, t: "On-Time Service" },
  { icon: Smile, t: "Customer Satisfaction Guaranteed" },
];

const STEPS = [
  { n: "01", t: "Book & Relax", d: "Book your kitchen partner on call or WhatsApp." },
  { n: "02", t: "Verified Cook Arrives on Time", d: "Trained & verified cook arrives on time." },
  { n: "03", t: "We Handle the Kitchen", d: "Focus on your guests or rest while we cook." },
];

const Services = () => {
  return (
    <main data-testid="services-page" className="bg-[#f7f5f2]">
      <Seo
        title="Kitchen Help Services in Lucknow — Prep, Cook, Party | ooloo"
        description="Kitchen prep, cooking, on-demand cook and family & party support in Lucknow. Trained ooloo cooks at your doorstep in 30 minutes. From ₹299/hour."
        path="/services"
      />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-12">
        <p className="eyebrow rise">What we do</p>
        <h1 className="font-display mt-5 text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl rise rise-1">
          Four services.
          <br />
          <span className="italic font-serif font-light text-[#c45b3e]">One promise</span>.
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[#5c5a56] leading-relaxed font-medium rise rise-2">
          Trained cooks. Trusted support. Hassle-free experience. From daily kitchen prep to big-family gatherings — we handle it, at your doorstep in 30 minutes.
        </p>
      </section>

      {/* SERVICE CARDS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16">
        <div className="grid md:grid-cols-2 gap-5">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            const isDark = s.accent === "dark";
            return (
              <div key={s.title} data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`} className={`${isDark ? "dark-card text-white" : "bento-card"} p-8 md:p-10`}>
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isDark ? "bg-white/10 text-[#c45b3e]" : "bg-[#efebe6] text-[#c45b3e]"}`}>
                  <Icon size={24} strokeWidth={1.8} />
                </div>
                <h2 className={`mt-6 font-display text-3xl md:text-4xl font-extrabold tracking-tight ${isDark ? "text-white" : "text-[#0a0a0a]"}`}>{s.title}</h2>
                <p className={`mt-3 ${isDark ? "text-white/80" : "text-[#5c5a56]"}`}>{s.desc}</p>
                <ul className={`mt-5 space-y-2 text-sm ${isDark ? "text-white/80" : "text-[#0a0a0a]"}`}>
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-[#c45b3e]" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Exclusion note */}
        <div className="mt-8 accent-card border-2 border-dashed border-[#c45b3e]/30 p-6">
          <div className="flex flex-col md:flex-row gap-4 md:items-center">
            <div className="flex items-start gap-3 md:flex-1">
              <XCircle size={22} className="text-[#c45b3e] mt-1 flex-shrink-0" />
              <div>
                <p className="font-display font-extrabold text-lg text-[#0a0a0a]">What we don&apos;t do</p>
                <p className="text-[#5c5a56] text-sm">To keep quality high, our cooks focus purely on cooking. We do <strong>not</strong> provide kitchen cleaning, utensil cleaning or serving.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {BRAND.exclusions.map((x) => (
                <span key={x} className="inline-flex items-center gap-1 bg-white border border-[#e5e2dc] rounded-full px-3 py-1.5 text-xs font-bold text-[#0a0a0a]">
                  <XCircle size={12} className="text-[#c45b3e]" /> {x}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="bg-[#0a0a0a] text-white py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-5">
              <p className="eyebrow text-[#c45b3e]">Flexible hourly booking</p>
              <h2 className="font-display mt-4 text-5xl md:text-6xl font-black tracking-tighter leading-[0.95]">
                Simple, honest
                <br />
                <span className="italic font-serif font-light text-[#c45b3e]">pricing</span>.
              </h2>
              <p className="mt-6 max-w-md text-white/70 leading-relaxed">
                Pay by the hour, no hidden charges. Book only what you need.
              </p>
              <a href={waLink("Hi ooloo, I want to book a cook.")} target="_blank" rel="noreferrer" data-testid="services-book-cta" className="btn-whatsapp mt-8">
                <MessageCircle size={18} strokeWidth={2.5} /> Book now
              </a>
            </div>
            <div className="lg:col-span-7 grid md:grid-cols-3 gap-4">
              {[
                { hr: "1 Hour", price: "₹299", cap: "Up to 3 people" },
                { hr: "2 Hours", price: "₹399", cap: "Up to 6 people", badge: "Popular" },
                { hr: "Extra 30 min", price: "₹99", cap: "Add anytime" },
              ].map((p) => (
                <div key={p.hr} data-testid={`hourly-${p.hr.toLowerCase().replace(/\s+/g, "-")}`} className="relative bg-white text-[#0a0a0a] rounded-2xl p-6 text-center">
                  {p.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c45b3e] text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">{p.badge}</span>}
                  <p className="text-xs font-bold tracking-widest uppercase text-[#5c5a56]">{p.hr}</p>
                  <p className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tighter text-[#c45b3e]">{p.price}</p>
                  <p className="mt-2 text-xs text-[#5c5a56]">{p.cap}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Party rate */}
          <div className="mt-12 bg-[#c45b3e] rounded-3xl p-8 md:p-10 grid lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8">
              <p className="text-xs font-bold tracking-widest uppercase text-white/70">Family gathering & party</p>
              <p className="mt-2 font-display text-3xl md:text-4xl font-black tracking-tighter">
                Up to 20 guests · 2 trained cooks · Min 3 hours
              </p>
            </div>
            <div className="lg:col-span-4 text-right">
              <p className="font-display text-5xl md:text-6xl font-black tracking-tighter">₹999<span className="text-xl text-white/80">/hr</span></p>
              <a href={waLink("Hi ooloo, I want to book cooks for a party.")} target="_blank" rel="noreferrer" data-testid="party-book-cta" className="inline-flex items-center gap-2 mt-4 bg-white text-[#0a0a0a] px-5 py-3 rounded-full font-bold text-sm">
                <MessageCircle size={16} strokeWidth={2.5} /> Book party cooks
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#efebe6] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="eyebrow">The ooloo experience</p>
            <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-[#0a0a0a]">3 easy <span className="italic font-serif font-light text-[#c45b3e]">steps</span>.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.n} data-testid={`process-step-${i + 1}`} className="bg-white rounded-3xl p-8 border border-[#e5e2dc] relative">
                <p className="font-display text-6xl font-black tracking-tighter text-[#c45b3e]/20 absolute top-4 right-6">{s.n}</p>
                <p className="font-mono text-xs font-bold tracking-widest uppercase text-[#c45b3e]">Step {s.n}</p>
                <h3 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-[#0a0a0a]">{s.t}</h3>
                <p className="mt-3 text-[#5c5a56]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow">Why choose ooloo</p>
          <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-[#0a0a0a]">Built on <span className="italic font-serif font-light text-[#c45b3e]">trust</span>.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
          {WHY_US.map((w) => {
            const Icon = w.icon;
            return (
              <div key={w.t} className="bento-card p-6 flex flex-col items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#efebe6] flex items-center justify-center text-[#c45b3e]">
                  <Icon size={20} strokeWidth={1.8} />
                </div>
                <p className="font-display text-base md:text-lg font-extrabold tracking-tight text-[#0a0a0a] leading-snug">{w.t}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0a0a0a] text-white py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter">
            Ready for kitchen help <span className="italic font-serif font-light text-[#c45b3e]">today?</span>
          </h2>
          <div className="flex gap-3 flex-wrap">
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="services-final-whatsapp" className="btn-whatsapp">
              <MessageCircle size={18} strokeWidth={2.5} /> Book on WhatsApp
            </a>
            <Link to="/plans" data-testid="services-see-plans" className="inline-flex items-center gap-2 bg-white text-[#0a0a0a] px-6 py-3.5 rounded-full font-bold hover:bg-[#c45b3e] hover:text-white transition">
              See smart plans <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Services;
